<template>
  <div class="mx-auto px-4 sm:px-4 lg:px-8">
    <BaseCard title="Pemilahan Stok Gudang">
      <div class="border-t pt-5">
        <div class="flex flex-col">
          <div class="flex items-center justify-end space-x-2">
            <base-search v-model="filter.search" @input="handleSearch" />
            <base-button @click="visibleMovementClassificationModal = true">
              <Icon class="h-5 w-5" icon="heroicons:plus-20-solid" />
              Pilah Barang
            </base-button>
          </div>
          <div class="mt-2 flex sm:-mx-6 lg:-mx-8">
            <div
              class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
            >
              <Datatable
                :total="getAll['classify-stocks'].meta.page.total"
                :perPage="getAll['classify-stocks'].meta.page.perPage"
                :currentPage="getAll['classify-stocks'].meta.page.currentPage"
                @pagechanged="handleChangePage"
                :meta="getAll['classify-stocks'].meta"
                :is-empty="!getAll['classify-stocks'].data?.length"
              >
                <template v-slot:thead>
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Nama Produk
                      </th>
                      <th
                        scope="col"
                        class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Tipe
                      </th>
                      <th
                        scope="col"
                        class="px-6 py-3 pr-24 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Total Produk
                      </th>
                    </tr>
                  </thead>
                </template>
                <template v-slot:tbody>
                  <div v-if="isLoading" class="flex items-center text-center">
                    <loading></loading>
                  </div>
                  <tbody v-if="getAll['classify-stocks'].data?.length > 0">
                    <tr
                      class="bg-white hover:bg-green-100"
                      v-for="(data, dataIdx) in getAll['classify-stocks'].data"
                      :key="data.id"
                      :class="dataIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    >
                      <td
                        class="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900"
                      >
                        {{ data.attributes.product_name }} ({{
                          data.attributes.product_code
                        }})
                      </td>
                      <td
                        class="whitespace-nowrap px-6 py-4 text-left text-sm text-gray-500"
                      >
                        {{
                          mapAttrToLabel[data.attributes.classification_type]
                        }}
                      </td>
                      <td
                        class="whitespace-nowrap px-6 py-4 pr-24 text-right text-sm text-gray-500"
                      >
                        {{ data.attributes.product_qty }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </Datatable>
            </div>
          </div>

          <stock-movement-modal
            :visible="visibleDetail"
            description="Data penerimaan barang dari Stockist"
            :with-dispute="false"
            force-display-first-packet
            @close="visibleDetail = false"
            @refresh="handleRefresh"
            with-pilah-barang
            @pilah-barang="onStockClassification"
          >
            <template #origin-office="{ originOffice }">
              <div class="sm:col-span-2">
                <dt class="text-sm font-bold text-gray-700">Nomor Stockist</dt>
                <dd class="mt-1 text-sm text-gray-900">
                  {{ originOffice.attributes.code }}
                </dd>
              </div>
            </template>
          </stock-movement-modal>
          <MovementClassificationModal
            :visible="visibleMovementClassificationModal"
            @close="onStockClassification"
            @refetch="loadStockMovements"
          />
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script>
import { debounce } from 'debounce';
import { mapGetters, mapActions } from 'vuex';
import Datatable from '@/components/base/Datatable';
import dayjs from 'dayjs';
import { sum } from '@/utils/array';
import StockMovementModal from '@/components/stock-movement/StockMovementModal.vue';
import BaseSearch from '@/components/base/Search.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import MovementClassificationModal from '@/components/modal/movement-classification-modal.vue';

export default {
  name: 'Home',
  components: {
    Datatable,
    StockMovementModal,
    BaseSearch,
    MovementClassificationModal,
    BaseButton,
  },

  data() {
    return {
      visibleDetail: false,
      visibleMovementClassificationModal: false,
      page: {
        number: 1,
        size: 5,
      },
      filter: {
        isReceived: false,
        search: null,
      },
      mapAttrToLabel: {
        damaged: 'Dari Stok Asli ke Stok Rusak',
        expired: 'Dari Stok Asli ke Stok Kadaluwarsa',
        suitable_for_sale: 'Dari Stok Asli ke Stok Layak Dijual',
        unsuitable_for_sale: 'Dari Stok Asli ke Stok Tidak Layak Dijual',
        free: 'Dari Stok Asli ke Stok Free',
        reimburse: 'Dari Stok Asli ke Stok Reimburse',
      },
    };
  },

  computed: {
    ...mapGetters({
      getAll: 'main/getAll',
      getStockMovements: 'stock_movements/getStockMovements',
      isLoading: 'stock_movements/getLoading',
    }),
    classifyStockParams() {
      return {
        schema: 'classify-stocks',
        params: {
          'page[size]': 5
        }
      };
    },
  },
  methods: {
    dayjs,
    ...mapActions({
      fetchAll: 'main/fetchAll',
      fetchStockMovements: 'stock_movements/fetchStockMovements',
      fetchStockMovement: 'stock_movements/fetchStockMovement',
    }),
    async handleClickItem(item) {
      const res = await this.fetchStockMovement({ id: item.id });

      if (res) {
        this.visibleDetail = true;
      }
    },
    handleChangePage(page) {
      this.loadStockMovements({
        'page[size]': 5,
        'page[number]': page
      });
    },
    handleSearch: debounce(function () {
      this.handleFilter();
    }, 300),
    handleFilter() {
      this.loadStockMovements({
        'filter[search]': this.filter.search
      });
    },
    handleRefresh() {
      this.resetFilter();
      this.loadStockMovements();

      this.visibleDetail = false;
    },
    resetPage() {
      this.page.size = 5;
      this.page.number = 1;
    },
    resetFilter() {
      this.filter.isReceived = false;
      this.filter.search = null;
    },
    countStockMovementWeight(stockMovement) {
      if (!stockMovement.attributes.packets) {
        return 0;
      }
      return sum(stockMovement.attributes.packets, (packet) => {
        return sum(packet.products, (product) => product.total_weight);
      });
    },
    countStockMovementPrice(stockMovement) {
      if (!stockMovement.attributes.packets) {
        return 0;
      }
      return sum(stockMovement.attributes.packets, (packet) => {
        return sum(packet.products, (product) => product.product_price);
      });
    },
    loadStockMovements(params) {
      this.fetchAll({ ...this.classifyStockParams, params });
    },
    onStockClassification({ allModal } = {}) {
      if (allModal) {
        this.visibleDetail = false;
        this.visibleMovementClassificationModal = false;
        return;
      }
      this.visibleDetail = !this.visibleDetail;
      this.visibleMovementClassificationModal =
        !this.visibleMovementClassificationModal;
    },
  },
  created() {
    this.fetchAll(this.classifyStockParams);
  },
};
</script>
